import { useState, useRef } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Sparkles, Share2, Loader2, Image as ImageIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const ShareableCardGenerator = ({ referralCode }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('gradient');
  const canvasRef = useRef(null);

  const cardStyles = [
    { id: 'gradient', name: 'Gradient', colors: ['#8B5CF6', '#EC4899', '#06B6D4'] },
    { id: 'dark', name: 'Dark', colors: ['#1E1E2E', '#2D2D44', '#3D3D55'] },
    { id: 'neon', name: 'Neon', colors: ['#00F0FF', '#FF00FF', '#FFFF00'] },
  ];

  const generateCard = async () => {
    setGenerating(true);
    
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      const width = 1200;
      const height = 630;
      canvas.width = width;
      canvas.height = height;
      
      const style = cardStyles.find(s => s.id === selectedStyle) || cardStyles[0];
      
      // Background gradient
      const gradient = ctx.createLinearGradient(0, 0, width, height);
      if (selectedStyle === 'gradient') {
        gradient.addColorStop(0, '#1a1a2e');
        gradient.addColorStop(0.5, '#16213e');
        gradient.addColorStop(1, '#0f0f23');
      } else if (selectedStyle === 'dark') {
        gradient.addColorStop(0, '#0f0f1a');
        gradient.addColorStop(1, '#1a1a2e');
      } else {
        gradient.addColorStop(0, '#0f0f1a');
        gradient.addColorStop(1, '#1a0a2e');
      }
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, width, height);
      
      // Decorative circles
      ctx.beginPath();
      ctx.arc(100, 100, 150, 0, Math.PI * 2);
      ctx.fillStyle = style.colors[0] + '20';
      ctx.fill();
      
      ctx.beginPath();
      ctx.arc(width - 100, height - 100, 200, 0, Math.PI * 2);
      ctx.fillStyle = style.colors[1] + '20';
      ctx.fill();
      
      // Logo text
      ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('iStudy', 80, 100);
      ctx.fillStyle = '#00d4ff';
      ctx.fillText('Pro', 195, 100);
      
      // Main heading
      ctx.font = 'bold 64px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#ffffff';
      ctx.fillText('Study Smarter with AI', 80, 250);
      
      // Subheading
      ctx.font = '32px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#a0a0b0';
      ctx.fillText('Your AI-powered study companion for lectures,', 80, 310);
      ctx.fillText('flashcards, and personalized learning', 80, 350);
      
      // Referral code box
      const boxX = 80;
      const boxY = 400;
      const boxWidth = 450;
      const boxHeight = 120;
      
      // Box background
      ctx.fillStyle = '#8B5CF620';
      ctx.strokeStyle = '#8B5CF650';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(boxX, boxY, boxWidth, boxHeight, 20);
      ctx.fill();
      ctx.stroke();
      
      // Code label
      ctx.font = '20px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#a0a0b0';
      ctx.fillText('USE REFERRAL CODE', boxX + 30, boxY + 40);
      
      // Code
      ctx.font = 'bold 48px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#00d4ff';
      ctx.fillText(referralCode || 'XXXXX', boxX + 30, boxY + 95);
      
      // Reward badge
      const badgeX = width - 350;
      const badgeY = 420;
      
      ctx.fillStyle = '#10B98120';
      ctx.strokeStyle = '#10B98150';
      ctx.beginPath();
      ctx.roundRect(badgeX, badgeY, 280, 100, 16);
      ctx.fill();
      ctx.stroke();
      
      ctx.font = '18px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#10B981';
      ctx.fillText('🎁 Get 7 Days FREE', badgeX + 20, badgeY + 40);
      ctx.fillText('Premium Access!', badgeX + 20, badgeY + 70);
      
      // Footer
      ctx.font = '20px system-ui, -apple-system, sans-serif';
      ctx.fillStyle = '#666';
      ctx.fillText('istudypro.net', 80, height - 40);
      
      setShowPreview(true);
    } catch (error) {
      console.error('Error generating card:', error);
      toast.error('Failed to generate card');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCard = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const link = document.createElement('a');
    link.download = `istudypro-referral-${referralCode}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
    toast.success('Card downloaded!');
  };

  const shareCardToSocial = async (platform) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const shareUrl = `${window.location.origin}/signup?ref=${referralCode}`;
    const shareText = `📚 Join me on iStudyPro and get 7 days FREE premium! Use my code: ${referralCode}`;
    
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=400');
  };

  return (
    <div className="mt-4 pt-4 border-t border-[var(--isp-glass-border)]">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="font-medium text-sm">Share on Social Media</h4>
          <p className="text-xs text-muted-foreground">Create a shareable card for your friends</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={generateCard}
          disabled={generating || !referralCode}
          className="rounded-lg"
          data-testid="generate-card-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImageIcon className="w-4 h-4 mr-2" />}
          Generate Card
        </Button>
      </div>
      
      {/* Style Selector */}
      <div className="flex gap-2 mb-3">
        {cardStyles.map(style => (
          <button
            key={style.id}
            onClick={() => setSelectedStyle(style.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              selectedStyle === style.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted/30 text-muted-foreground hover:bg-muted/50'
            }`}
          >
            {style.name}
          </button>
        ))}
      </div>
      
      {/* Canvas (hidden until generated) */}
      <canvas ref={canvasRef} className="hidden" />
      
      {/* Preview */}
      {showPreview && (
        <div className="space-y-3">
          <div className="rounded-xl overflow-hidden border border-[var(--isp-glass-border)]">
            <img 
              src={canvasRef.current?.toDataURL()} 
              alt="Referral Card Preview" 
              className="w-full"
            />
          </div>
          
          {/* Action buttons */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadCard} className="rounded-lg flex-1">
              Download
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => shareCardToSocial('twitter')}
              className="rounded-lg"
            >
              𝕏
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => shareCardToSocial('facebook')}
              className="rounded-lg"
            >
              f
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => shareCardToSocial('linkedin')}
              className="rounded-lg"
            >
              in
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => shareCardToSocial('whatsapp')}
              className="rounded-lg"
            >
              📱
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareableCardGenerator;
